// components/ProjectExpenditures.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import Papa from 'papaparse'; // Import papaparse for CSV generation

import Table from '../../shared/components/layout/Table';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import NoDataFound from '../components/layout/NoDataFound';
import { AccountsReportCsvDownloadButton } from '../components/AccountsReportCsvDownloadButton';
import { useAccountsReportToolbar } from '../contexts/AccountsReportToolbarContext';
import PaginationComponent from '../../shared/components/PaginationComponent';
import { transformData } from '../../super-admin/utils/transformData';
import { getProjectExpendituresReports } from '../services/api/implementation/impl';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const ProjectExpenditures = () => {
  const userId = Cookies.get('userId');
  const { setTabEndAction } = useAccountsReportToolbar();
  /*
   * -------------------------------------------------
   * Component State
   * -------------------------------------------------
   */
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // Items per page
  const [isDownloading, setIsDownloading] = useState(false); // New state for download status
  const [dotCount, setDotCount] = useState(0); // State for dots animation

  /*
   * -------------------------------------------------
   * Fetching Project Expenditures from the API (Server-Side Pagination)
   * -------------------------------------------------
   */
  const {
    data: expenditureResponse,
    isFetching: isFetchingExpenditures,
    isError,
    error
  } = useQuery({
    queryKey: ['projectExpenditures', page, limit],
    queryFn: () => getProjectExpendituresReports(page, limit),
    keepPreviousData: true,
    refetchOnMount: 'always'
  });

  // If your API returns { data: [...], meta: {...} }
  const allExpenditures = expenditureResponse?.data || [];
  const totalItems = expenditureResponse?.meta?.total || 0;
  const totalPages = Math.ceil(totalItems / limit);

  /*
   * -------------------------------------------------
   * Transform the Data for Table
   * -------------------------------------------------
   */
  const expendituresDataFormat = ['id', 'BlockName', 'ProjectName', 'NoOfBills', 'TotalExpense'];

  const processedExpenditures = allExpenditures.map((expenditure) => ({
    id: expenditure.id || 'no-id',
    BlockName: expenditure.blockName || 'N/A',
    ProjectName: expenditure.projectName || 'N/A',
    NoOfBills: expenditure.numberOfBills || 0,
    TotalExpense: expenditure.totalExpense !== undefined ? `₹ ${expenditure.totalExpense}` : '₹ 0'
  }));

  const expendituresData = processedExpenditures.map((row) =>
    transformData(row, expendituresDataFormat)
  );

  /*
   * -------------------------------------------------
   * Handlers
   * -------------------------------------------------
   */
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  /**
   * Handle Download CSV using papaparse
   */
  const handleDownload = async () => {
    if (isDownloading) return; // Prevent multiple clicks

    setIsDownloading(true);
    try {
      // Fetch all data with page=1 and limit=1000
      const response = await getProjectExpendituresReports(1, 1000);
      const expenditures = response?.data || [];

      if (expenditures.length === 0) {
        alert('No data available to download.');
        return;
      }

      // Define CSV headers
      const headers = ['Block Name', 'Project Name', 'Number of Bills', 'Total Expense'];

      // Map data to CSV rows as objects without the currency symbol
      const rows = expenditures.map((expenditure) => ({
        'Block Name': expenditure.blockName || 'N/A',
        'Project Name': expenditure.projectName || 'N/A',
        'Number of Bills': expenditure.numberOfBills || 0,
        'Total Expense': expenditure.totalExpense !== undefined ? expenditure.totalExpense : 0
      }));

      // Convert to CSV using papaparse
      const csv = Papa.unparse({
        fields: headers,
        data: rows
      });

      // Create a Blob from the CSV content
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

      // Create a temporary link to trigger the download
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute('download', `project_expenditures.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (downloadError) {
      console.error('Download failed:', downloadError);
      alert('Failed to download the file. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  /*
   * -------------------------------------------------
   * Dots Animation Effect
   * -------------------------------------------------
   */

  // Track page view
  useEffect(() => {
    mixpanel.track('Project Expenditures Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    let interval;
    if (isDownloading) {
      interval = setInterval(() => {
        setDotCount((prev) => (prev < 3 ? prev + 1 : 0));
      }, 500);
    } else {
      setDotCount(0);
    }

    return () => clearInterval(interval);
  }, [isDownloading]);

  const renderDownloadingText = () => {
    return `Downloading${'.'.repeat(dotCount)}`;
  };

  const handleDownloadRef = useRef(handleDownload);
  handleDownloadRef.current = handleDownload;

  useEffect(() => {
    setTabEndAction(
      <AccountsReportCsvDownloadButton
        onClick={() => handleDownloadRef.current()}
        disabled={isDownloading}
        statusText={isDownloading ? renderDownloadingText() : null}
      />
    );
    return () => setTabEndAction(null);
  }, [setTabEndAction, isDownloading, dotCount]);

  /*
   * -------------------------------------------------
   * Render
   * -------------------------------------------------
   */
  return (
    <div className="w-full flex flex-col z-5 bg-gray-100 pb-10 overflow-hidden">
      {/* Expenditures Table */}
      <div className="w-full h-full">
        {isFetchingExpenditures ? (
          <div className="pt-12 text-center">
            <p>Loading...</p>
          </div>
        ) : isError ? (
          <div className="pt-12 text-center text-red-500">
            <p>Error: {error.message}</p>
          </div>
        ) : expendituresData && expendituresData.length > 0 ? (
          <GridWrapper width={100}>
            <div className="min-h-[26rem] h-[calc(100vh-12rem)] max-h-[28rem] relative">
              <Table
                headers={['Block Name', 'Project Name', 'No. of Bills', 'Total Expense']}
                data={expendituresData}
                identifier="id"
                isViewEnable={false}
                isDeleteEnable={false}
                isEditEnable={false}
                isWrap={true}
              />
            </div>
            <div className="relative pt-3">
              <PaginationComponent
                page={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                isFetching={isFetchingExpenditures}
              />
            </div>
          </GridWrapper>
        ) : (
          <NoDataFound disabledBtn screenHeight={false} />
        )}
      </div>
    </div>
  );
};

export default ProjectExpenditures;
